/**
 * AutoGenerator.ts
 * Procedural dungeon generation with archetype-driven layout strategies.
 * Pure logic — no Babylon.js dependencies, fully testable.
 *
 * Pipeline: activate cells → MST connectivity → extra loops → type assignment
 */

import type { GridCell, GridPos, CellType, CellTheme, OctileDungeon } from '../types/OctileTypes.ts'
import type { DungeonPersonality } from '../types/GrowthTypes.ts'
import { createDungeon, activateCell, setWall, getCell, getActiveOctagons, countActiveCells } from '../grid/OctileGrid.ts'
import { isFullyConnected, canTraverse } from '../grid/PathFinder.ts'
import {
    getCellType, inBounds, getNeighbors, gridKey,
    getOctWallToward, getConnWallToward
} from '../grid/GridAddressing.ts'
import { SeededRandom } from '../utils/SeededRandom.ts'

// ─── Types ──────────────────────────────────────────────────

export type LayoutArchetype = 'linear' | 'branching' | 'hub' | 'loop' | 'maze'

export interface GenerationParams {
    archetype: LayoutArchetype
    personality: DungeonPersonality
    targetCells: number       // 5-40 octagon cells
    difficulty: number        // 1-10 max difficulty
    seed: number
    loopProbability: number   // 0-1, extra connections beyond MST
    branchProbability: number // 0-1, side branch frequency
}

export interface GenerationResult {
    dungeon: OctileDungeon
    stats: {
        activatedOctagons: number
        activatedConnectors: number
        totalWallsOpened: number
        entrancePos: GridPos
        bossPos: GridPos | null
        isFullyConnected: boolean
    }
}

interface OctConnection {
    target: GridPos
    connector: GridPos | null  // null for cardinal, GridPos for diagonal
}

interface MSTEdge {
    from: GridPos
    to: GridPos
    connector: GridPos | null
}

// ─── Personality Weight Tables ──────────────────────────────

const PERSONALITY_TYPES: Record<DungeonPersonality, {
    primary: CellType[], secondary: CellType[], fill: CellType[]
}> = {
    aggressive: {
        primary: ['guard_post', 'arena', 'nest'],
        secondary: ['patrol_route', 'ambush', 'barricade'],
        fill: ['cave', 'chamber', 'passage'],
    },
    cunning: {
        primary: ['trap_floor', 'puzzle', 'mimic_den'],
        secondary: ['secret', 'trap_walls', 'trap_ceiling'],
        fill: ['cave', 'alcove', 'passage'],
    },
    defensive: {
        primary: ['barricade', 'gate', 'guard_post'],
        secondary: ['pillared_hall', 'trap_walls', 'prison'],
        fill: ['chamber', 'cave', 'passage'],
    },
    hoarding: {
        primary: ['vault', 'cache', 'hoard'],
        secondary: ['trap_floor', 'guard_post', 'mimic_den'],
        fill: ['cave', 'chamber', 'passage'],
    },
    expansive: {
        primary: ['cave', 'chamber', 'pillared_hall'],
        secondary: ['junction', 'mushroom_grotto', 'water_pool'],
        fill: ['cave', 'passage', 'alcove'],
    },
}

// ─── AutoGenerator ──────────────────────────────────────────

export class AutoGenerator {
    generate(params: GenerationParams): GenerationResult {
        const rng = new SeededRandom(params.seed)
        const dungeon = createDungeon('Untitled Dungeon', 10, 10, params.seed)
        dungeon.dna.personality = params.personality
        const grid = dungeon.layers[0].grid
        const gridWidth = dungeon.gridWidth
        const gridHeight = dungeon.gridHeight

        // Step 1: Activate octagon cells per archetype
        const activated = this.activateCells(grid, gridWidth, gridHeight, params, rng)

        // Step 2: Build MST to guarantee connectivity, bridging gaps if needed
        let mstEdges = this.buildMST(grid, gridWidth, gridHeight, activated, rng)
        let wallsOpened = 0
        for (const edge of mstEdges) {
            wallsOpened += this.openWallBetween(grid, edge.from, edge.to, edge.connector)
        }

        // Bridge disconnected components by adding intermediate cells
        wallsOpened += this.bridgeGaps(grid, gridWidth, gridHeight, activated, mstEdges, rng)

        // Step 3: Add extra loops beyond MST
        wallsOpened += this.addLoops(grid, gridWidth, gridHeight, activated, mstEdges, params.loopProbability, rng)

        // Step 4: Assign types based on BFS distance + personality
        const { entrancePos, bossPos } = this.assignTypes(grid, gridWidth, gridHeight, activated, params, rng)

        // Step 5: Add thematic doors on connectors
        this.addDoors(grid, params.personality, rng)

        // Update dungeon entrance
        dungeon.entrance = { ...entrancePos, layer: 0 }

        const counts = countActiveCells(grid)
        return {
            dungeon,
            stats: {
                activatedOctagons: counts.octagons,
                activatedConnectors: counts.connectors,
                totalWallsOpened: wallsOpened,
                entrancePos,
                bossPos,
                isFullyConnected: isFullyConnected(grid, entrancePos),
            },
        }
    }

    // ─── Cell Activation (Archetype Strategies) ─────────────

    private activateCells(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        params: GenerationParams, rng: SeededRandom
    ): GridPos[] {
        switch (params.archetype) {
            case 'linear':    return this.activateLinear(grid, gridWidth, gridHeight, params.targetCells, rng)
            case 'branching': return this.activateBranching(grid, gridWidth, gridHeight, params.targetCells, rng)
            case 'hub':       return this.activateHub(grid, gridWidth, gridHeight, params.targetCells, rng)
            case 'loop':      return this.activateLoop(grid, gridWidth, gridHeight, params.targetCells, rng)
            case 'maze':      return this.activateMaze(grid, gridWidth, gridHeight, params.targetCells, rng)
        }
    }

    private activateLinear(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        targetCells: number, rng: SeededRandom
    ): GridPos[] {
        const activated: GridPos[] = []
        const activeSet = new Set<string>()

        // Pick a starting edge octagon
        const edges: GridPos[] = []
        for (let r = 0; r < gridHeight; r += 2) {
            for (let c = 0; c < gridWidth; c += 2) {
                if (r === 0 || r === gridHeight - 1 || c === 0 || c === gridWidth - 1) {
                    edges.push({ row: r, col: c })
                }
            }
        }
        const start = rng.pick(edges)

        // Pick a preferred direction
        const directions = [
            { dr: -2, dc: 0 }, { dr: 2, dc: 0 },
            { dr: 0, dc: -2 }, { dr: 0, dc: 2 },
        ]
        // Prefer moving toward center
        const centerRow = Math.floor(gridHeight / 2)
        const centerCol = Math.floor(gridWidth / 2)
        const preferred = directions.reduce((best, d) => {
            const distBest = Math.abs(start.row + best.dr - centerRow) + Math.abs(start.col + best.dc - centerCol)
            const distD = Math.abs(start.row + d.dr - centerRow) + Math.abs(start.col + d.dc - centerCol)
            return distD < distBest ? d : best
        })

        const addCell = (pos: GridPos) => {
            const key = gridKey(pos.row, pos.col)
            if (!activeSet.has(key)) {
                activateCell(grid, pos.row, pos.col)
                activated.push(pos)
                activeSet.add(key)
            }
        }

        addCell(start)
        let current = start

        for (let attempts = 0; attempts < targetCells * 5 && activated.length < targetCells; attempts++) {
            let dir: { dr: number; dc: number }
            const roll = rng.next()
            if (roll < 0.7) {
                dir = preferred
            } else if (roll < 0.9) {
                // Perpendicular
                const perps = directions.filter(d =>
                    d.dr !== preferred.dr || d.dc !== preferred.dc
                ).filter(d =>
                    d.dr !== -preferred.dr || d.dc !== -preferred.dc
                )
                dir = rng.pick(perps.length > 0 ? perps : directions)
            } else {
                dir = rng.pick(directions)
            }

            const nr = current.row + dir.dr
            const nc = current.col + dir.dc
            if (!inBounds(nr, nc, gridWidth, gridHeight)) continue
            if (getCellType(nr, nc) !== 'octagon') continue

            addCell({ row: nr, col: nc })
            current = { row: nr, col: nc }
        }

        return activated
    }

    private activateBranching(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        targetCells: number, rng: SeededRandom
    ): GridPos[] {
        const activated: GridPos[] = []
        const activeSet = new Set<string>()

        const addCell = (pos: GridPos) => {
            const key = gridKey(pos.row, pos.col)
            if (activeSet.has(key)) return false
            activateCell(grid, pos.row, pos.col)
            activated.push(pos)
            activeSet.add(key)
            return true
        }

        // Start near center
        const startRow = 8 + (rng.intRange(-2, 2) * 2)
        const startCol = 8 + (rng.intRange(-2, 2) * 2)
        addCell({ row: startRow, col: startCol })

        // Build main spine (60-70% of target)
        const spineTarget = Math.ceil(targetCells * rng.floatRange(0.55, 0.7))
        let current = { row: startRow, col: startCol }

        const allDirs = [
            { dr: -2, dc: 0 }, { dr: 2, dc: 0 },
            { dr: 0, dc: -2 }, { dr: 0, dc: 2 },
            { dr: -2, dc: 2 }, { dr: 2, dc: 2 },
            { dr: 2, dc: -2 }, { dr: -2, dc: -2 },
        ]

        for (let attempts = 0; attempts < spineTarget * 4 && activated.length < spineTarget; attempts++) {
            const dir = rng.pick(allDirs)
            const nr = current.row + dir.dr
            const nc = current.col + dir.dc
            if (!inBounds(nr, nc, gridWidth, gridHeight)) continue
            if (getCellType(nr, nc) !== 'octagon') continue

            addCell({ row: nr, col: nc })
            current = { row: nr, col: nc }
        }

        // Add side branches from random spine cells
        const spine = [...activated]
        for (const cell of spine) {
            if (activated.length >= targetCells) break
            if (!rng.chance(0.4)) continue

            const branchLen = rng.intRange(1, 3)
            let branchCurrent = cell
            for (let b = 0; b < branchLen && activated.length < targetCells; b++) {
                const dir = rng.pick(allDirs)
                const nr = branchCurrent.row + dir.dr
                const nc = branchCurrent.col + dir.dc
                if (!inBounds(nr, nc, gridWidth, gridHeight)) break
                if (getCellType(nr, nc) !== 'octagon') break

                addCell({ row: nr, col: nc })
                branchCurrent = { row: nr, col: nc }
            }
        }

        return activated
    }

    private activateHub(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        targetCells: number, rng: SeededRandom
    ): GridPos[] {
        const activated: GridPos[] = []
        const activeSet = new Set<string>()

        const addCell = (pos: GridPos) => {
            const key = gridKey(pos.row, pos.col)
            if (activeSet.has(key)) return false
            activateCell(grid, pos.row, pos.col)
            activated.push(pos)
            activeSet.add(key)
            return true
        }

        // Central hub
        const hubRow = 8 + (rng.intRange(-1, 1) * 2)
        const hubCol = 8 + (rng.intRange(-1, 1) * 2)
        addCell({ row: hubRow, col: hubCol })

        // Pick 3-5 spoke directions
        const allDirs = [
            { dr: -2, dc: 0 }, { dr: 2, dc: 0 },
            { dr: 0, dc: -2 }, { dr: 0, dc: 2 },
            { dr: -2, dc: 2 }, { dr: 2, dc: 2 },
            { dr: 2, dc: -2 }, { dr: -2, dc: -2 },
        ]
        const spokeCount = rng.intRange(3, Math.min(5, Math.ceil(targetCells / 3)))
        const shuffledDirs = rng.shuffle([...allDirs])
        const spokeDirs = shuffledDirs.slice(0, spokeCount)

        // Distribute remaining cells across spokes
        const cellsPerSpoke = Math.floor((targetCells - 1) / spokeCount)
        const extraCells = (targetCells - 1) % spokeCount

        for (let s = 0; s < spokeDirs.length && activated.length < targetCells; s++) {
            const dir = spokeDirs[s]
            const spokeLen = cellsPerSpoke + (s < extraCells ? 1 : 0)
            let current = { row: hubRow, col: hubCol }

            for (let step = 0; step < spokeLen && activated.length < targetCells; step++) {
                const nr = current.row + dir.dr
                const nc = current.col + dir.dc
                if (!inBounds(nr, nc, gridWidth, gridHeight)) break
                if (getCellType(nr, nc) !== 'octagon') break

                addCell({ row: nr, col: nc })
                current = { row: nr, col: nc }
            }
        }

        return activated
    }

    private activateLoop(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        targetCells: number, rng: SeededRandom
    ): GridPos[] {
        const activated: GridPos[] = []
        const activeSet = new Set<string>()

        const addCell = (pos: GridPos) => {
            const key = gridKey(pos.row, pos.col)
            if (activeSet.has(key)) return false
            activateCell(grid, pos.row, pos.col)
            activated.push(pos)
            activeSet.add(key)
            return true
        }

        const centerRow = 8 + (rng.intRange(-1, 1) * 2)
        const centerCol = 8 + (rng.intRange(-1, 1) * 2)
        const radius = Math.max(2, Math.ceil(Math.sqrt(targetCells) / 2))

        // Define 8 waypoints at compass points around the center, snapped to octagon positions
        const rawWaypoints = [
            { row: centerRow - radius * 2, col: centerCol },                          // N
            { row: centerRow - radius * 2, col: centerCol + radius * 2 },             // NE
            { row: centerRow,              col: centerCol + radius * 2 },             // E
            { row: centerRow + radius * 2, col: centerCol + radius * 2 },             // SE
            { row: centerRow + radius * 2, col: centerCol },                          // S
            { row: centerRow + radius * 2, col: centerCol - radius * 2 },             // SW
            { row: centerRow,              col: centerCol - radius * 2 },             // W
            { row: centerRow - radius * 2, col: centerCol - radius * 2 },             // NW
        ]

        // Snap to nearest valid octagon and clamp to grid bounds
        const waypoints: GridPos[] = []
        const waypointKeys = new Set<string>()
        for (const w of rawWaypoints) {
            const snapRow = Math.max(0, Math.min(gridHeight - 1, Math.round(w.row / 2) * 2))
            const snapCol = Math.max(0, Math.min(gridWidth - 1, Math.round(w.col / 2) * 2))
            if (inBounds(snapRow, snapCol, gridWidth, gridHeight) &&
                getCellType(snapRow, snapCol) === 'octagon') {
                const key = gridKey(snapRow, snapCol)
                if (!waypointKeys.has(key)) {
                    waypoints.push({ row: snapRow, col: snapCol })
                    waypointKeys.add(key)
                }
            }
        }

        if (waypoints.length < 3) {
            // Fallback: just activate cells near center
            addCell({ row: centerRow, col: centerCol })
            return activated
        }

        // Walk from waypoint to waypoint, including closing the loop back to start
        addCell(waypoints[0])
        let current = waypoints[0]
        const ringTarget = Math.ceil(targetCells * 0.75)

        for (let wi = 1; wi <= waypoints.length && activated.length < ringTarget; wi++) {
            const target = waypoints[wi % waypoints.length]

            // Walk toward this waypoint
            for (let steps = 0; steps < 15; steps++) {
                if (current.row === target.row && current.col === target.col) break

                // Find neighbor closest to target
                const connections = this.getOctConnections(current.row, current.col, gridWidth, gridHeight)
                let bestConn: OctConnection | null = null
                let bestDist = Infinity
                for (const conn of connections) {
                    const dist = Math.abs(conn.target.row - target.row) +
                                 Math.abs(conn.target.col - target.col)
                    if (dist < bestDist) {
                        bestDist = dist
                        bestConn = conn
                    }
                }
                if (!bestConn) break

                addCell(bestConn.target)
                current = bestConn.target
            }
        }

        // Fill interior with remaining cells
        const allDirs = [
            { dr: -2, dc: 0 }, { dr: 2, dc: 0 },
            { dr: 0, dc: -2 }, { dr: 0, dc: 2 },
        ]
        if (activated.length > 2) {
            current = activated[Math.floor(activated.length / 2)]
        }
        for (let attempts = 0; attempts < targetCells * 4 && activated.length < targetCells; attempts++) {
            const dir = rng.pick(allDirs)
            const nr = current.row + dir.dr
            const nc = current.col + dir.dc
            if (!inBounds(nr, nc, gridWidth, gridHeight)) continue
            if (getCellType(nr, nc) !== 'octagon') continue

            addCell({ row: nr, col: nc })
            current = { row: nr, col: nc }
        }

        return activated
    }

    private activateMaze(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        targetCells: number, rng: SeededRandom
    ): GridPos[] {
        const activated: GridPos[] = []
        const activeSet = new Set<string>()

        // Calculate square region size centered on grid
        const side = Math.ceil(Math.sqrt(targetCells))
        const clampedSide = Math.min(side, 10)  // Max 10x10 octagons

        // Center the region
        const startOctRow = Math.max(0, Math.floor((10 - clampedSide) / 2))
        const startOctCol = Math.max(0, Math.floor((10 - clampedSide) / 2))

        // Activate all octagons in the rectangular region
        for (let or = 0; or < clampedSide; or++) {
            for (let oc = 0; oc < clampedSide; oc++) {
                const row = (startOctRow + or) * 2
                const col = (startOctCol + oc) * 2
                if (inBounds(row, col, gridWidth, gridHeight)) {
                    activateCell(grid, row, col)
                    activated.push({ row, col })
                    activeSet.add(gridKey(row, col))
                }
            }
        }

        return activated
    }

    // ─── MST (Prim's Algorithm) ─────────────────────────────

    private buildMST(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        cells: GridPos[], rng: SeededRandom
    ): MSTEdge[] {
        if (cells.length <= 1) return []

        const cellSet = new Set(cells.map(c => gridKey(c.row, c.col)))
        const inTree = new Set<string>()
        const edges: MSTEdge[] = []

        // Start with first cell
        inTree.add(gridKey(cells[0].row, cells[0].col))

        while (inTree.size < cellSet.size) {
            // Find all candidate edges from tree to non-tree
            const candidates: Array<MSTEdge & { weight: number }> = []

            for (const key of inTree) {
                const [r, c] = key.split(',').map(Number)
                const connections = this.getOctConnections(r, c, gridWidth, gridHeight)

                for (const conn of connections) {
                    const targetKey = gridKey(conn.target.row, conn.target.col)
                    if (!cellSet.has(targetKey)) continue
                    if (inTree.has(targetKey)) continue

                    // Cardinal edges get slightly lower weight (prefer cleaner layouts)
                    const baseWeight = conn.connector ? 1.2 : 1.0
                    candidates.push({
                        from: { row: r, col: c },
                        to: conn.target,
                        connector: conn.connector,
                        weight: baseWeight + rng.next() * 0.5,
                    })
                }
            }

            if (candidates.length === 0) break  // Disconnected cells

            // Pick minimum weight
            candidates.sort((a, b) => a.weight - b.weight)
            const best = candidates[0]

            inTree.add(gridKey(best.to.row, best.to.col))
            edges.push({ from: best.from, to: best.to, connector: best.connector })
        }

        return edges
    }

    // ─── Bridge Disconnected Components ────────────────────

    private bridgeGaps(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        cells: GridPos[], mstEdges: MSTEdge[], rng: SeededRandom
    ): number {
        let wallsOpened = 0

        // Find connected component from first cell
        const reachable = new Set<string>()
        const queue: GridPos[] = [cells[0]]
        reachable.add(gridKey(cells[0].row, cells[0].col))
        while (queue.length > 0) {
            const curr = queue.shift()!
            const neighbors = getNeighbors(curr.row, curr.col, gridWidth, gridHeight)
            for (const n of neighbors) {
                const nk = gridKey(n.row, n.col)
                if (reachable.has(nk)) continue
                if (!canTraverse(grid, curr, n)) continue
                reachable.add(nk)
                queue.push(n)
            }
        }

        const cellSet = new Set(cells.map(c => gridKey(c.row, c.col)))
        const unreachable = cells.filter(c => !reachable.has(gridKey(c.row, c.col)))

        // For each unreachable cell, find the closest reachable cell and bridge
        for (const orphan of unreachable) {
            if (reachable.has(gridKey(orphan.row, orphan.col))) continue  // already fixed

            // Find closest reachable cell by Manhattan distance
            let bestDist = Infinity
            let bestTarget: GridPos | null = null
            for (const key of reachable) {
                const [r, c] = key.split(',').map(Number)
                if (getCellType(r, c) !== 'octagon') continue
                const dist = Math.abs(r - orphan.row) + Math.abs(c - orphan.col)
                if (dist < bestDist) {
                    bestDist = dist
                    bestTarget = { row: r, col: c }
                }
            }

            if (!bestTarget) continue

            // Try direct connection first
            const conns = this.getOctConnections(bestTarget.row, bestTarget.col, gridWidth, gridHeight)
            const directConn = conns.find(c =>
                c.target.row === orphan.row && c.target.col === orphan.col
            )
            if (directConn) {
                wallsOpened += this.openWallBetween(grid, bestTarget, orphan, directConn.connector)
                mstEdges.push({ from: bestTarget, to: orphan, connector: directConn.connector })
                reachable.add(gridKey(orphan.row, orphan.col))
                continue
            }

            // Build a path of intermediate cells from bestTarget toward orphan
            let current = bestTarget
            for (let steps = 0; steps < 10; steps++) {
                const dr = orphan.row - current.row
                const dc = orphan.col - current.col
                if (dr === 0 && dc === 0) break

                // Pick best direction toward orphan
                const possibleConns = this.getOctConnections(current.row, current.col, gridWidth, gridHeight)
                let bestConn: OctConnection | null = null
                let bestConnDist = Infinity
                for (const conn of possibleConns) {
                    const d = Math.abs(conn.target.row - orphan.row) + Math.abs(conn.target.col - orphan.col)
                    if (d < bestConnDist) {
                        bestConnDist = d
                        bestConn = conn
                    }
                }
                if (!bestConn) break

                // Activate and connect
                activateCell(grid, bestConn.target.row, bestConn.target.col)
                if (!cellSet.has(gridKey(bestConn.target.row, bestConn.target.col))) {
                    cells.push(bestConn.target)
                    cellSet.add(gridKey(bestConn.target.row, bestConn.target.col))
                }
                wallsOpened += this.openWallBetween(grid, current, bestConn.target, bestConn.connector)
                mstEdges.push({ from: current, to: bestConn.target, connector: bestConn.connector })
                reachable.add(gridKey(bestConn.target.row, bestConn.target.col))
                current = bestConn.target

                if (current.row === orphan.row && current.col === orphan.col) break
            }
        }

        return wallsOpened
    }

    // ─── Extra Loops ────────────────────────────────────────

    private addLoops(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        cells: GridPos[], mstEdges: MSTEdge[],
        loopProbability: number, rng: SeededRandom
    ): number {
        // Find all possible edges not in the MST
        const mstSet = new Set(mstEdges.map(e =>
            `${gridKey(e.from.row, e.from.col)}-${gridKey(e.to.row, e.to.col)}`
        ))
        const mstSetReverse = new Set(mstEdges.map(e =>
            `${gridKey(e.to.row, e.to.col)}-${gridKey(e.from.row, e.from.col)}`
        ))

        const cellSet = new Set(cells.map(c => gridKey(c.row, c.col)))
        let wallsOpened = 0

        for (const cell of cells) {
            const connections = this.getOctConnections(cell.row, cell.col, gridWidth, gridHeight)
            for (const conn of connections) {
                const targetKey = gridKey(conn.target.row, conn.target.col)
                if (!cellSet.has(targetKey)) continue

                const edgeKey = `${gridKey(cell.row, cell.col)}-${targetKey}`
                if (mstSet.has(edgeKey) || mstSetReverse.has(edgeKey)) continue

                if (rng.chance(loopProbability * 0.3)) {
                    wallsOpened += this.openWallBetween(grid, cell, conn.target, conn.connector)
                    mstSet.add(edgeKey)  // Prevent duplicates
                }
            }
        }

        return wallsOpened
    }

    // ─── Type Assignment ────────────────────────────────────

    private assignTypes(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        cells: GridPos[], params: GenerationParams, rng: SeededRandom
    ): { entrancePos: GridPos, bossPos: GridPos | null } {
        // BFS distances from first activated cell (temporary entrance)
        const distances = this.bfsDistances(grid, gridWidth, gridHeight, cells[0])
        const maxDist = Math.max(...Array.from(distances.values()))

        // Find entrance: the cell with lowest row (top of map) that's accessible
        let entrancePos = cells[0]
        for (const cell of cells) {
            if (cell.row < entrancePos.row ||
                (cell.row === entrancePos.row && cell.col < entrancePos.col)) {
                entrancePos = cell
            }
        }

        // Recompute distances from actual entrance
        const entranceDists = this.bfsDistances(grid, gridWidth, gridHeight, entrancePos)
        const maxEntDist = Math.max(...Array.from(entranceDists.values()), 1)

        // Find boss: furthest from entrance
        let bossPos: GridPos | null = null
        let maxBossDist = 0
        for (const cell of cells) {
            const dist = entranceDists.get(gridKey(cell.row, cell.col)) ?? 0
            if (dist > maxBossDist) {
                maxBossDist = dist
                bossPos = cell
            }
        }

        // Find shrine: near midpoint distance
        let shrinePos: GridPos | null = null
        let bestShrineDelta = Infinity
        const targetShrineDist = maxEntDist * 0.5
        for (const cell of cells) {
            if (cell === entrancePos || cell === bossPos) continue
            const dist = entranceDists.get(gridKey(cell.row, cell.col)) ?? 0
            const delta = Math.abs(dist - targetShrineDist)
            if (delta < bestShrineDelta) {
                bestShrineDelta = delta
                shrinePos = cell
            }
        }

        // Assign types
        const personalityInfo = PERSONALITY_TYPES[params.personality]

        for (const cell of cells) {
            const gridCell = getCell(grid, cell.row, cell.col)
            if (!gridCell || gridCell.kind !== 'octagon') continue

            const dist = entranceDists.get(gridKey(cell.row, cell.col)) ?? 0
            const ratio = maxEntDist > 0 ? dist / maxEntDist : 0
            const cellDifficulty = Math.max(1, Math.ceil(ratio * params.difficulty))

            if (cell.row === entrancePos.row && cell.col === entrancePos.col) {
                gridCell.type = 'entrance'
                gridCell.theme = 'ancient_stone'
                gridCell.difficulty = 1
            } else if (bossPos && cell.row === bossPos.row && cell.col === bossPos.col) {
                gridCell.type = 'boss_throne'
                gridCell.theme = 'volcanic'
                gridCell.difficulty = params.difficulty
            } else if (shrinePos && cell.row === shrinePos.row && cell.col === shrinePos.col) {
                gridCell.type = 'shrine'
                gridCell.theme = 'ancient_stone'
                gridCell.difficulty = cellDifficulty
            } else {
                // Pick type by personality + distance
                const type = this.pickTypeByPersonality(personalityInfo, ratio, rng)
                gridCell.type = type
                gridCell.theme = this.pickTheme(ratio, rng)
                gridCell.difficulty = cellDifficulty
            }
        }

        return { entrancePos, bossPos }
    }

    private pickTypeByPersonality(
        info: { primary: CellType[], secondary: CellType[], fill: CellType[] },
        distanceRatio: number, rng: SeededRandom
    ): CellType {
        // Higher difficulty areas get more primary types
        const primaryWeight = 1 + distanceRatio * 3
        const secondaryWeight = 2
        const fillWeight = 3 - distanceRatio * 2

        const roll = rng.next() * (primaryWeight + secondaryWeight + fillWeight)
        if (roll < primaryWeight) return rng.pick(info.primary)
        if (roll < primaryWeight + secondaryWeight) return rng.pick(info.secondary)
        return rng.pick(info.fill)
    }

    private pickTheme(distanceRatio: number, rng: SeededRandom): CellTheme {
        const themes: CellTheme[] = distanceRatio < 0.3
            ? ['natural_cave', 'ancient_stone', 'overgrown']
            : distanceRatio < 0.7
                ? ['ancient_stone', 'crystal', 'organic', 'frozen']
                : ['volcanic', 'shadow', 'crystal', 'ancient_stone']
        return rng.pick(themes)
    }

    // ─── Door Placement ─────────────────────────────────────

    private addDoors(grid: GridCell[][], personality: DungeonPersonality, rng: SeededRandom): void {
        const doorChance = personality === 'defensive' ? 0.25 : personality === 'cunning' ? 0.2 : 0.12

        for (const row of grid) {
            for (const cell of row) {
                if (!cell?.active || cell.kind !== 'connector') continue

                if (rng.chance(doorChance)) {
                    for (let w = 0; w < cell.walls.length; w++) {
                        if (cell.walls[w] === 'OPEN') {
                            cell.walls[w] = 'DOOR'
                        }
                    }
                }
            }
        }
    }

    // ─── Wall Opening ───────────────────────────────────────

    private openWallBetween(
        grid: GridCell[][], from: GridPos, to: GridPos,
        connector: GridPos | null
    ): number {
        let count = 0

        if (!connector) {
            // Cardinal: direct oct-to-oct
            const fromWall = getOctWallToward(from.row, from.col, to.row, to.col)
            const toWall = getOctWallToward(to.row, to.col, from.row, from.col)
            if (fromWall !== -1) { setWall(grid, from.row, from.col, fromWall, 'OPEN'); count++ }
            if (toWall !== -1) { setWall(grid, to.row, to.col, toWall, 'OPEN'); count++ }
        } else {
            // Diagonal: oct → connector → oct
            activateCell(grid, connector.row, connector.col)

            const fromToConn = getOctWallToward(from.row, from.col, connector.row, connector.col)
            const connToFrom = getConnWallToward(connector.row, connector.col, from.row, from.col)
            const connToTo = getConnWallToward(connector.row, connector.col, to.row, to.col)
            const toToConn = getOctWallToward(to.row, to.col, connector.row, connector.col)

            if (fromToConn !== -1) { setWall(grid, from.row, from.col, fromToConn, 'OPEN'); count++ }
            if (connToFrom !== -1) { setWall(grid, connector.row, connector.col, connToFrom, 'OPEN'); count++ }
            if (connToTo !== -1) { setWall(grid, connector.row, connector.col, connToTo, 'OPEN'); count++ }
            if (toToConn !== -1) { setWall(grid, to.row, to.col, toToConn, 'OPEN'); count++ }
        }

        return count
    }

    // ─── Helpers ────────────────────────────────────────────

    private getOctConnections(
        row: number, col: number,
        gridWidth: number, gridHeight: number
    ): OctConnection[] {
        const result: OctConnection[] = []

        // Cardinal: direct oct-to-oct (step ±2)
        for (const [dr, dc] of [[-2, 0], [2, 0], [0, -2], [0, 2]]) {
            const tr = row + dr
            const tc = col + dc
            if (inBounds(tr, tc, gridWidth, gridHeight) && getCellType(tr, tc) === 'octagon') {
                result.push({ target: { row: tr, col: tc }, connector: null })
            }
        }

        // Diagonal: oct → connector → oct (step ±1 then ±1)
        for (const [dr, dc] of [[-1, -1], [-1, 1], [1, -1], [1, 1]]) {
            const cr = row + dr
            const cc = col + dc  // connector position
            const tr = row + 2 * dr
            const tc = col + 2 * dc  // far octagon
            if (inBounds(tr, tc, gridWidth, gridHeight) && getCellType(tr, tc) === 'octagon') {
                result.push({ target: { row: tr, col: tc }, connector: { row: cr, col: cc } })
            }
        }

        return result
    }

    private bfsDistances(
        grid: GridCell[][], gridWidth: number, gridHeight: number,
        start: GridPos
    ): Map<string, number> {
        const distances = new Map<string, number>()
        const queue: GridPos[] = [start]
        distances.set(gridKey(start.row, start.col), 0)

        while (queue.length > 0) {
            const current = queue.shift()!
            const currentDist = distances.get(gridKey(current.row, current.col))!
            const neighbors = getNeighbors(current.row, current.col, gridWidth, gridHeight)

            for (const neighbor of neighbors) {
                const nKey = gridKey(neighbor.row, neighbor.col)
                if (distances.has(nKey)) continue
                if (!canTraverse(grid, current, neighbor)) continue
                distances.set(nKey, currentDist + 1)
                queue.push(neighbor)
            }
        }

        return distances
    }
}
